import { ChevronRight } from 'lucide-react'
import { cn } from '../../lib/cn.js'

export interface OpenLinkProps {
  children: React.ReactNode
  onClick: () => void
  /** A chevron after the words. On a row in a list; not on a name in a line. */
  chevron?: boolean
  className?: string
}

/**
 * Words that open something, and look like it.
 *
 * The treatment was written by hand in three places and copied from a fourth,
 * which is how a desk ends up with four not-quite-identical links. The
 * substance of it — `text-coral-text`, underline on hover, a focus ring — is
 * from the brand note, which is explicit that Nectarine is the action colour
 * everywhere.
 *
 * A **button**, not an anchor: the desk has no URLs. And the same rule as
 * the overview's attention rows, which had to learn it the hard way: a name
 * that opens a record in the same ink as every other word on the page gives
 * no sign it can be pressed until the cursor happens to land on it.
 */
export function OpenLink({ children, onClick, chevron, className }: OpenLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        /* A line of text is a 21px target. The padding grows it and the
           negative margin gives the space back, so a table row does not get
           taller and a sentence around one does not move. */
        '-my-2 py-2',
        'text-coral-text inline-flex items-center gap-1 rounded-sm text-left',
        'underline-offset-2 outline-none hover:underline',
        'focus-visible:ring-2 focus-visible:ring-coral-fill/40',
        className,
      )}
    >
      {children}
      {chevron && <ChevronRight className="size-4 shrink-0 opacity-60" aria-hidden />}
    </button>
  )
}
